/* eslint-disable prefer-promise-reject-errors */
import { Plugin } from "@tiptap/pm/state";
import { Node, ReactNodeViewRenderer } from "@tiptap/react";

import { ActionButton } from "../../components";
import ImageUploader from "./components/ImageUploader";
import {
	UploadImagesPlugin,
	createImageUpload,
	handleImageDrop,
	handleImagePaste
} from "../../plugins/image-upload";

const DEFAULT_OPTIONS = {
	acceptMimes: ["image/jpeg", "image/gif", "image/png", "image/jpg"],
	maxSize: 1024 * 1024 * 5
};

export const ImageUpload = Node.create({
	name: "imageUpload",
	isolating: true,
	defining: true,
	group: "block",
	draggable: false,
	selectable: true,
	inline: false,

	onCreate() {
		if (typeof this.options.upload !== "function") {
			throw new TypeError("Image upload function should be a function");
		}
	},

	parseHTML() {
		return [{ tag: `div[data-type="${this.name}"]` }];
	},

	renderHTML() {
		return ["div", { "data-type": this.name }];
	},

	addCommands() {
		return {
			setImageUpload:
				() =>
				({ commands }) =>
					commands.insertContent(`<div data-type="${this.name}"></div>`)
		};
	},

	addNodeView() {
		return ReactNodeViewRenderer(ImageUploader);
	},

	addOptions() {
		return {
			...DEFAULT_OPTIONS,
			...this.parent?.(),
			upload: () => Promise.reject("Image Upload Function"),
			button: ({ editor, extension, t }) => ({
				component: ActionButton,
				componentProps: {
					action: () => editor.commands.setImageUpload(),
					upload: extension.options.upload,
					icon: "ImageUp",
					tooltip: t("editor.image.tooltip")
				}
			})
		};
	},

	addProseMirrorPlugins() {
		const validateFile = file => {
			if (!this.options.acceptMimes.includes(file.type)) {
				return false;
			}
			if (file.size > this.options.maxSize) {
				return false;
			}
			return true;
		};

		const uploadFn = createImageUpload({
			validateFn: validateFile,
			onUpload: this.options.upload
		});

		return [
			new Plugin({
				props: {
					handlePaste: (view, event) => {
						if (!event.clipboardData) {
							return false;
						}
						const items = [...(event.clipboardData.items || [])];
						if (items.some(x => x.type === "text/html")) {
							return false;
						}
						return handleImagePaste(view, event, uploadFn);
					},
					handleDrop: (view, event, _, moved) => {
						if (!(event instanceof DragEvent) || !event.dataTransfer) {
							return false;
						}
						handleImageDrop(view, event, moved, uploadFn);
						return false;
					}
				}
			}),
			UploadImagesPlugin()
		];
	}
});

export default ImageUpload;
